/**
 * Marker functionality.
 *
 * THIS SOFTWARE IS PROVIDED "AS IS" AND WITHOUT ANY EXPRESS OR IMPLIED
 * WARRANTIES, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
 *
 * @copyright Copyright (c) 2018 - 2023 fusionLib. All rights reserved.
 * @link http://fusionlib.com
 */

import { flMaps } from "./fusionLib.maps.core.js";

/**
 * Maker object for use on the map.
 *
 * @param {flMaps.Map} map The map to show the marker on.
 * @param {number} lat number The latitude.
 * @param {number} lng number The longitude.
 * @param {map} opts Optional options.
 */
flMaps.Marker = function(map, lat, lng, opts) {
	opts = opts || {};
	this.id = opts.id || '';
	this.data = opts.data || '';

	var self = this;
	this.map = map;

	if(this.map.driver === 'google') {
		this._marker = new google.maps.Marker({
			position: { lat: lat, lng: lng },
			map: this.map._map,
			draggable: opts.draggable || false
		});

		if(opts.onDragend) {
			this._marker.addListener('dragend', function() {
				opts.onDragend(self);
			});
		}

		if(opts.onClicked) {
			this._marker.addListener('click', function() {
				opts.onClicked(self);
			});
		}
	}
	else {
		this._marker = new mapboxgl.Marker({
			draggable: opts.draggable || false
		})
			.setLngLat([lng, lat])
			.addTo(this.map._map)

		if(opts.onDragend) {
			this._marker.on('dragend', function() {
				opts.onDragend(self);
			});
		}

		if(opts.onClicked) {
			var el = this._marker.getElement();
			el.style.cursor = 'pointer';
			el.addEventListener('click', function(e) {
				e.preventDefault();
				e.stopPropagation();
				opts.onClicked(self);
			});
		}
	}

	/**
	 * Get position of the marker.
	 *
	 * @returns {{lat:number, lng:number}}
	 */
	this.getPosition = function() {
		if(this.map.driver === 'google') {
			var p = this._marker.getPosition();
			return { lat: p.lat(), lng: p.lng() };
		}
		else {
			return this._marker.getLngLat();
		}
	};

	/**
	 * Set the position of the marker.
	 *
	 * @param {number} lat The latitude.
	 * @param {number} lng The longitude.
	 * @returns {flMaps.Marker}
	 */
	this.setPosition = function(lat, lng) {
		if(this.map.driver === 'google') {
			this._marker.setPosition({ lat: lat, lng: lng });
		}
		else {
			this._marker.setLngLat([lng, lat]);
		}
		return this;
	};

	/**
	 * Add the marker to a map.
	 * @param {flMaps.Map} map The map to add to.
	 * @returns {flMaps.Marker}
	 */
	this.addTo = function(map) {
		// Remove from the current map if any
		if(this.map)
			this.remove();

		this.map = map;
		if(this.map.driver === 'google') {
			this._marker.setMap(this.map._map);
		}
		else {
			this._marker.addTo(this.map._map);
		}

		return this;
	};

	/**
	 * Remove the marker from the map
	 */
	this.remove = function() {
		if(this.map.driver === 'google') {
			this._marker.setMap(null);
		}
		else {
			this._marker.remove();
		}
		this.map = null;
	};
};
